import React, { useState, useEffect } from 'react';

interface CategoryLoadingIndicatorProps {
  isVisible: boolean;
  hasResult: boolean;
  category?: string;
  description?: string;
  error?: string;
}

const CategoryLoadingIndicator: React.FC<CategoryLoadingIndicatorProps> = ({
  isVisible,
  hasResult,
  category,
  description,
  error
}) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isVisible || hasResult) return;

    let animationFrame: number;
    let startTime: number;
    const duration = 14000; // 14 seconds total
    const maxProgress = 90; // Don't go above 90% until result comes back

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      // Use easing function for more realistic progress
      // Accelerate early, then slow down as it approaches 90%
      const t = Math.min(elapsed / duration, 1);
      const easedProgress = t < 0.5 
        ? 2 * t * t // Quadratic acceleration for first half
        : 1 - Math.pow(-2 * t + 2, 2) / 2; // Quadratic deceleration for second half
      
      const currentProgress = Math.floor(easedProgress * maxProgress);
      setProgress(currentProgress);

      if (elapsed < duration && !hasResult) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, hasResult]);

  // Jump to 100% when result is available
  useEffect(() => {
    if (hasResult && !isComplete) {
      setProgress(100);
      setIsComplete(true);
    }
  }, [hasResult, isComplete]);

  if (!isVisible) return null;

  return (
    <div className="mt-2 p-2 bg-gray-50 rounded text-xs transition-all duration-500">
      {!hasResult ? (
        // Loading state
        <div className="space-y-2">
          <div className="flex items-center justify-between text-gray-600">
            <span className="font-medium">AI Analysis Processing...</span>
            <span className="text-xs">{progress}%</span>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-full rounded-full transition-all duration-300 ease-out"
              style={{ 
                width: `${progress}%`,
                transform: `translateX(${progress < 100 ? '0' : '0'})` 
              }}
            />
          </div>
          
          {/* Loading animation dots */}
          <div className="flex items-center space-x-1 text-gray-500">
            <span>Analyzing image content</span>
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
              <div className="w-1 h-1 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
              <div className="w-1 h-1 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      ) : error ? (
        // Error state
        <div className="text-red-600 text-xs animate-fadeIn">
          Analysis failed: {error}
        </div>
      ) : (
        // Success state with results
        <div className="animate-fadeIn">
          <div className="font-semibold text-purple-600 mb-1">
            Category: {category}
          </div>
          <div className="text-gray-700">
            {description}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryLoadingIndicator;